import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { getTermsDocumentApi } from "../../services/axios/AuthApis/UserMasterApis";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import * as pdfjs from 'pdfjs-dist';
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const THEME = '#26256b';
const THEME_LIGHT = '#3230a0';

const Spinner = ({ size = 36 }) => (
    <>
        <style>{`@keyframes tkspin{to{transform:rotate(360deg)}}`}</style>
        <div style={{
            width: size, height: size, borderRadius: "50%",
            border: `3px solid ${THEME}22`,
            borderTopColor: THEME,
            animation: "tkspin 0.7s linear infinite",
            flexShrink: 0,
        }} />
    </>
);

const ConsentForm = ({ onAccept, onDeny, user, loading }) => {
    const scrollRef = useRef(null);
    const [scrolled, setScrolled] = useState(false);
    const [termsDocumentUrl, setTermsDocumentUrl] = useState("");
    const [termsLoading, setTermsLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);
    const [viewerWidth, setViewerWidth] = useState(900);

    const handleScroll = () => {
        const element = scrollRef.current;
        if (!element) return;
        setScrolled(element.scrollHeight - element.scrollTop - element.clientHeight < 24);
    };

    const onDocumentLoadSuccess = ({ numPages: loadedPages }) => {
        setNumPages(loadedPages);
        setTimeout(handleScroll, 0);
    };

    useEffect(() => {
        const loadTermsDocument = async () => {
            try {
                const response = await getTermsDocumentApi();
                setTermsDocumentUrl(response?.document_url || "");
            } catch {
                setTermsDocumentUrl("");
            } finally {
                setTermsLoading(false);
            }
        };
        loadTermsDocument();
    }, []);

    useEffect(() => {
        const updateWidth = () => {
            if (!scrollRef.current) return;
            setViewerWidth(Math.max(280, scrollRef.current.clientWidth - 32));
        };
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, []);

    return (
        <div style={{
            minHeight: "100vh",
            background: "#f8f9fc",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px 16px",
            fontFamily: "'Inter', sans-serif",
        }}>
            {/* Brand */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
                <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    backgroundColor: THEME,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <span style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>T</span>
                </div>
                <span style={{ color: THEME, fontWeight: 700, fontSize: 20 }}>Tanikkai GRC</span>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                style={{
                    width: "100%",
                    maxWidth: 780,
                    background: "#fff",
                    borderRadius: 20,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(38,37,107,0.08)",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div style={{
                    background: THEME,
                    padding: "26px 32px",
                    position: "relative",
                    overflow: "hidden",
                }}>
                    <div style={{
                        position: "absolute", top: -40, right: -40,
                        width: 180, height: 180, borderRadius: "50%",
                        background: `radial-gradient(circle, ${THEME_LIGHT}88 0%, transparent 70%)`,
                        pointerEvents: "none",
                    }} />
                    <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{
                            width: 48, height: 48, borderRadius: 12,
                            background: "rgba(255,255,255,0.15)",
                            border: "1.5px solid rgba(255,255,255,0.25)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                        }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                        </div>
                        <div>
                            <h1 style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.3px" }}>
                                Terms of Service &amp; Privacy Policy
                            </h1>
                            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, margin: "4px 0 0" }}>
                                Please review and accept our terms to continue
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: "28px 32px" }}>
                    {/* Document viewer */}
                    <div
                        ref={scrollRef}
                        onScroll={handleScroll}
                        style={{
                            height: 480,
                            overflowY: "auto",
                            border: `1.5px solid ${THEME}22`,
                            borderRadius: 12,
                            background: "#fafafa",
                            marginBottom: 14,
                        }}
                    >
                        {termsLoading ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                <Spinner />
                            </div>
                        ) : termsDocumentUrl ? (
                            <div style={{ padding: 16 }}>
                                <Document
                                    file={termsDocumentUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    loading={
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 360 }}>
                                            <Spinner />
                                        </div>
                                    }
                                    error={
                                        <div style={{ fontSize: 13, color: "#dc2626", padding: 16 }}>
                                            Unable to render Terms of Service document.
                                        </div>
                                    }
                                >
                                    {Array.from(new Array(numPages), (_, index) => (
                                        <div key={`page_${index + 1}`} style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
                                            <Page pageNumber={index + 1} width={viewerWidth} />
                                        </div>
                                    ))}
                                </Document>
                            </div>
                        ) : (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontSize: 13, color: "#dc2626" }}>
                                Unable to load Terms of Service document.
                            </div>
                        )}
                    </div>

                    {/* Scroll reminder */}
                    {!scrolled && (
                        <motion.div
                            animate={{ y: [0, 4, 0] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            style={{
                                display: "flex", alignItems: "center", justifyContent: "center",
                                gap: 6, color: "#d97706", fontSize: 13, marginBottom: 14,
                            }}
                        >
                            <ExclamationCircleIcon style={{ width: 16, height: 16 }} />
                            <span>Please scroll to the end of the document to continue</span>
                        </motion.div>
                    )}

                    {/* Privacy notice */}
                    <div style={{
                        background: `${THEME}09`,
                        border: `1px solid ${THEME}22`,
                        borderRadius: 10,
                        padding: "12px 16px",
                        marginBottom: 16,
                    }}>
                        <p style={{ fontSize: 13, color: THEME, margin: 0, lineHeight: 1.6 }}>
                            🔒 <span style={{ fontWeight: 600 }}>Your Privacy Matters:</span> We will never share your data with third parties without your explicit consent.
                        </p>
                    </div>

                    {/* User info */}
                    {user && (
                        <div style={{
                            background: "#f8f9fc",
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: "12px 16px",
                            marginBottom: 20,
                        }}>
                            <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 3px" }}>Signed in as</p>
                            <p style={{ fontSize: 14, fontWeight: 600, color: "#111827", margin: "0 0 2px" }}>{user.email}</p>
                            <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
                                Role: <span style={{ textTransform: "capitalize" }}>{user.role}</span>
                            </p>
                        </div>
                    )}

                    {/* Buttons */}
                    <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                        <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={onDeny}
                            disabled={loading || !scrolled}
                            style={{
                                flex: 1, padding: "12px 20px",
                                border: "1.5px solid #d1d5db",
                                background: "#fff",
                                color: "#374151",
                                borderRadius: 10,
                                fontWeight: 500,
                                fontSize: 14,
                                cursor: (loading || !scrolled) ? "not-allowed" : "pointer",
                                opacity: (loading || !scrolled) ? 0.5 : 1,
                                fontFamily: "'Inter', sans-serif",
                                transition: "all 0.15s",
                            }}
                        >
                            Decline
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={onAccept}
                            disabled={loading || !scrolled}
                            style={{
                                flex: 2, padding: "12px 20px",
                                background: (loading || !scrolled) ? "#9b9acc" : THEME,
                                color: "#fff",
                                border: "none",
                                borderRadius: 10,
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: (loading || !scrolled) ? "not-allowed" : "pointer",
                                boxShadow: (loading || !scrolled) ? "none" : `0 4px 14px ${THEME}55`,
                                fontFamily: "'Inter', sans-serif",
                                transition: "all 0.15s",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            }}
                        >
                            {loading ? (
                                <>
                                    <Spinner size={16} />
                                    Processing...
                                </>
                            ) : "Accept & Continue"}
                        </motion.button>
                    </div>

                    <p style={{ textAlign: "center", fontSize: 12, color: "#9ca3af", margin: 0 }}>
                        By clicking "Accept &amp; Continue", you acknowledge that you have read and agree to our terms
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default ConsentForm;
